'use client';

import Image from 'next/image';
import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';

import { PageBlogOverviewSchema } from '@/sanity/pages/PageBlogOverviewTemplate';
import { richTextComponent } from '@/sanity/sanityPortableText';
import heroMobileBackground from '@/public/global/hero-mobile-background.png';
import heroDesktopBackground from '@/public/blog/hero-desktop-background.png';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function BlogOverviewHero({ data }: { data: PageBlogOverviewSchema }) {
  return (
    <section className="relative flex w-full flex-col items-center overflow-hidden text-center max-lg:pb-[4rem] max-lg:pt-[8.75rem] lg:pb-[22.5rem] lg:pt-[20.625rem]">
      <motion.div
        initial={{ opacity: 0, filter: 'blur(8px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 1.2, ease }}
        className="pointer-events-none absolute inset-0 z-0 lg:hidden"
        aria-hidden="true"
      >
        <Image src={heroMobileBackground} alt="" className="h-full w-full object-cover object-top" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, filter: 'blur(8px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 1.2, ease }}
        className="pointer-events-none absolute inset-x-0 -top-[8.375rem] z-0 h-[64.375rem] max-lg:hidden"
        aria-hidden="true"
      >
        <Image src={heroDesktopBackground} alt="" className="h-full w-full object-cover object-top" />
      </motion.div>
      <div
        className="pointer-events-none absolute inset-x-0 top-[22.625rem] z-0 h-[66.625rem] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#fff_59.09%)] max-lg:hidden"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto flex w-[min(94.25rem,calc(100%-4rem))] flex-col items-center gap-[1.25rem]">
        <div className="flex w-full flex-col items-center gap-[1.25rem]">
          {data.heroChip && (
            <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-black">
              {data.heroChip}
            </p>
          )}
          {data.heroTitle && (
            <div className="richtext w-full prose-h1:mb-0 [&_h1_.italic_span]:!font-blacker max-lg:prose-h1:text-[5rem] max-lg:prose-h1:tracking-[-0.25rem] max-lg:[&_h1_.italic]:tracking-[-0.15rem] lg:[&_h1_.italic]:ml-[0.375rem] lg:[&_h1_.italic]:text-[6.625rem] lg:[&_h1_.italic]:leading-none lg:[&_h1_.italic]:tracking-[-0.2rem] lg:[&_h1_.italic>span]:pl-[1.125rem] lg:[&_h1_.italic>span]:![background:linear-gradient(#E2F3EF,#E2F3EF)_bottom/100%_3.5rem_no-repeat] lg:[&_h1_.italic]:after:content-['_'] lg:[&_h1_.italic]:after:[word-spacing:1.25rem] lg:[&_h1_.italic]:after:[background:linear-gradient(#E2F3EF,#E2F3EF)_left_bottom/1.125rem_3.5rem_no-repeat]">
              <PortableText value={data.heroTitle} components={richTextComponent} />
            </div>
          )}
        </div>
        {data.heroSubtitle && (
          <p className="mb-0 whitespace-pre-line font-geist text-[2rem] font-medium leading-[1.5] text-black/70 [text-wrap:wrap] max-lg:w-[min(42.25rem,100%)] lg:w-[min(63.75rem,100%)]">
            {data.heroSubtitle}
          </p>
        )}
      </div>
    </section>
  );
}
